import { useEffect, useState } from "react";
import { keyName, transposeKey } from "../../theory/key";
import { MAX_BPM, MIN_BPM } from "../../sequencer/time";
import { Panel } from "../components/controls";
import { useDirty, useServices, useSetlists } from "../context";

const confirmDiscard = () => window.confirm("You have unsaved changes. Discard them and open this song?");
const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

export function Band() {
  const { setlists } = useServices();
  const state = useSetlists();
  const [selectedId, setSelectedId] = useState<string | null>(state.active?.setlistId ?? null);
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState("");
  useEffect(() => { void setlists.refreshSongs(); }, [setlists]);
  const selected = state.setlists.find((s) => s.id === selectedId) ?? null;

  if (!state.loaded) return <p role="status">Loading setlists…</p>;

  return (
    <div className="workspace-grid band">
      <Panel title="Setlists">
        <form className="controls-row" onSubmit={async (e) => {
          e.preventDefault();
          const s = await setlists.create(newName, newDate || null);
          if (s) { setSelectedId(s.id); setNewName(""); setNewDate(""); }
        }}>
          <input aria-label="New setlist name" placeholder="e.g. Sunday morning" value={newName} maxLength={120} onChange={(e) => setNewName(e.currentTarget.value)} />
          <input type="date" aria-label="Date (optional)" value={newDate} onChange={(e) => setNewDate(e.currentTarget.value)} />
          <button type="submit" className="primary">New setlist</button>
        </form>
        {state.setlists.length === 0 ? <p className="hint">No setlists yet. Save a few arrangements in Projects, then build a setlist here.</p> : (
          <ul className="setlist-list">
            {state.setlists.map((s) => (
              <li key={s.id}>
                <button type="button" className={s.id === selectedId ? "active" : ""} aria-pressed={s.id === selectedId} onClick={() => setSelectedId(s.id)}>
                  <strong>{s.name}</strong> <span className="hint">{s.date ?? "no date"} · {s.entries.length} song{s.entries.length === 1 ? "" : "s"}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <SongLibrary targetId={selected?.id ?? null} targetName={selected?.name ?? null} />

      {selected && <SetlistEditor key={selected.id} setlistId={selected.id} onDeleted={() => setSelectedId(null)} />}
    </div>
  );
}

function SongLibrary({ targetId, targetName }: { targetId: string | null; targetName: string | null }) {
  const { setlists, library } = useServices();
  const { songs } = useSetlists();
  const dirty = useDirty();
  return (
    <Panel title="Songs">
      {songs.length === 0 ? <p className="hint">No saved arrangements yet. Set a song title in Projects → Song details and save.</p> : (
        <ul className="song-list">
          {songs.map((s) => (
            <li key={s.id}>
              <span className="song-title"><strong>{s.title || s.name}</strong>{s.artist && <span className="hint"> — {s.artist}</span>}</span>
              <span className="hint">{s.title && s.title !== s.name ? `${s.name} · ` : ""}{keyName(transposeKey(s.key, s.transposeSemitones))} · {s.tempoBpm} BPM</span>
              <span className="row-actions">
                <button type="button" disabled={!targetId} onClick={() => targetId && void setlists.addSong(targetId, s.id)}
                  aria-label={targetName ? `Add ${s.title || s.name} to ${targetName}` : `Add ${s.title || s.name} (select a setlist first)`}>Add</button>
                <button type="button" onClick={() => { if (!dirty || confirmDiscard()) void library.open(s.id); }} aria-label={`Open ${s.title || s.name}`}>Open</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function SetlistEditor({ setlistId, onDeleted }: { setlistId: string; onDeleted: () => void }) {
  const { setlists } = useServices();
  const state = useSetlists();
  const s = state.setlists.find((x) => x.id === setlistId)!;
  const activeIndex = state.active?.setlistId === s.id ? state.active.index : -1;

  return (
    <Panel title={`Setlist: ${s.name}`} className="span-all"
      actions={<>
        <button type="button" className="primary" disabled={s.entries.length === 0} onClick={() => void setlists.open(s.id, 0, confirmDiscard)}>▶ Start setlist</button>
        <button type="button" onClick={async () => { if (window.confirm(`Delete setlist "${s.name}"? Songs are not deleted.`)) { await setlists.remove(s.id); onDeleted(); } }}>Delete</button>
      </>}>
      <div className="controls-row">
        <label className="inline-field"><span>Name</span>
          <input defaultValue={s.name} maxLength={120} onBlur={(e) => { const v = e.currentTarget.value.trim(); if (v && v !== s.name) void setlists.update(s.id, (x) => ({ ...x, name: v })); }} />
        </label>
        <label className="inline-field"><span>Date</span>
          <input type="date" defaultValue={s.date ?? ""} onChange={(e) => void setlists.update(s.id, (x) => ({ ...x, date: e.currentTarget.value || null }))} />
        </label>
      </div>
      {s.entries.length === 0 ? <p className="hint">Add songs from the Songs list.</p> : (
        <ol className="setlist-entries">
          {s.entries.map((e, i) => {
            const song = setlists.song(e.projectId);
            const label = song ? song.title || song.name : "Missing arrangement";
            const baseT = song?.transposeSemitones ?? 0;
            return (
              <li key={e.id} className={i === activeIndex ? "current" : ""}>
                <span className="entry-num" aria-hidden="true">{i + 1}</span>
                <div className="entry-main">
                  <strong className={song ? "" : "missing"}>{label}</strong>
                  {song && (
                    <div className="controls-row entry-controls">
                      <label className="inline-field"><span>Key</span>
                        <select value={e.transposeSemitones === null ? "own" : String(e.transposeSemitones)} aria-label={`Performance key for ${label}`}
                          onChange={(ev) => void setlists.updateEntry(s.id, e.id, { transposeSemitones: ev.currentTarget.value === "own" ? null : Number(ev.currentTarget.value) })}>
                          <option value="own">{`Arrangement: ${keyName(transposeKey(song.key, baseT))}`}</option>
                          {Array.from({ length: 13 }, (_, k) => k - 6).map((t) => (
                            <option key={t} value={t}>{`${keyName(transposeKey(song.key, t))} (${signed(t)} from ${song.key.tonic})`}</option>
                          ))}
                        </select>
                      </label>
                      <label className="inline-field"><span>Tempo</span>
                        <input type="number" min={MIN_BPM} max={MAX_BPM} placeholder={String(song.tempoBpm)} defaultValue={e.tempoBpm ?? ""} aria-label={`Tempo for ${label} (blank = arrangement's ${song.tempoBpm} BPM)`}
                          onBlur={(ev) => { const raw = ev.currentTarget.value; const v = raw === "" ? null : Math.round(Number(raw)); if (v === null || (v >= MIN_BPM && v <= MAX_BPM)) void setlists.updateEntry(s.id, e.id, { tempoBpm: v }); }} />
                      </label>
                      <input className="entry-notes" placeholder="Notes (e.g. capo 2, extra chorus)" aria-label={`Notes for ${label}`} defaultValue={e.notes} maxLength={1000}
                        onBlur={(ev) => { if (ev.currentTarget.value !== e.notes) void setlists.updateEntry(s.id, e.id, { notes: ev.currentTarget.value }); }} />
                    </div>
                  )}
                </div>
                <span className="row-actions">
                  <button type="button" disabled={!song} onClick={() => void setlists.open(s.id, i, confirmDiscard)} aria-label={`Open ${label}`}>Open</button>
                  <button type="button" disabled={i === 0} onClick={() => void setlists.moveEntry(s.id, e.id, -1)} aria-label={`Move ${label} up`}>↑</button>
                  <button type="button" disabled={i === s.entries.length - 1} onClick={() => void setlists.moveEntry(s.id, e.id, 1)} aria-label={`Move ${label} down`}>↓</button>
                  <button type="button" onClick={() => void setlists.removeEntry(s.id, e.id)} aria-label={`Remove ${label} from setlist`}>✕</button>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      <p className="hint">Key and tempo set here are applied when a song is opened from the setlist (shown as unsaved changes you can keep or undo). They never change the arrangement on their own.</p>
    </Panel>
  );
}
